import type { Placement } from '@floating-ui/react';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FaCaretDown, FaCheck } from 'react-icons/fa';
import { ChakraV3 } from '..';
import { type SelectGroup, type SelectOption, useSelect } from '../hooks/useSelect';
import { FloatingPopover } from './FloatingPopover';
import { ScrollArea } from './ScrollArea';
import { Spin } from './Spin';

export type SelectButtonVariant = ChakraV3.ButtonProps['variant'] | 'selectOutline' | 'selectGhost';

export type { SelectOption };

export type OptionVariant = 'tag' | 'text';

export interface SelectComponents<A, B, C extends Error = Error> {
  Loading?: () => React.ReactElement | null;
  Error?: (props: { error: C }) => React.ReactElement | null;
  Value?: (props: { option: SelectOption<A> }) => React.ReactElement | null;
  Placeholder?: (props: { placeholder?: string }) => React.ReactElement | null;
  NoOptions?: () => React.ReactElement | null;
  OptionGroup?: (props: { group: SelectGroup<A, B> }) => React.ReactElement | null;
  Option?: (props: { option: SelectOption<A> }) => React.ReactElement | null;
  Header?: (props: ChakraV3.StackProps) => React.ReactElement | null;
  Footer?: (props: ChakraV3.StackProps) => React.ReactElement | null;
}

export const Loading = () => (
  <ChakraV3.Text
    px={2}
    py={1}
    fontSize='sm'
    fontWeight='semibold'
    color='gray.500'
    userSelect='none'
  >
    Loading...
  </ChakraV3.Text>
);

export const SelectError = () => (
  <ChakraV3.Text px={2} py={1} fontSize='sm' fontWeight='semibold' userSelect='none'>
    Error loading options
  </ChakraV3.Text>
);

export const SelectContainer = (props: ChakraV3.BoxProps) => {
  const { children, ...rest } = props;

  return (
    <ChakraV3.Box minW='3xs' w='full' h='auto' rounded='md' bg='white' shadow='lg' {...rest}>
      {props.children}
    </ChakraV3.Box>
  );
};

export const SelectItem = React.forwardRef<HTMLLIElement, ChakraV3.ListItemProps>((props, ref) => {
  const { children, ...rest } = props;

  return (
    <ChakraV3.chakra.li
      ref={ref}
      {...rest}
      className='group'
      listStyleType='none'
      px={2}
      py={1}
      fontSize='sm'
      rounded='md'
      userSelect='none'
      scrollMarginY='2'
      _active={{
        bg: 'gray.100',
      }}
      _checked={{
        fontWeight: 'semibold',
      }}
      _disabled={{
        color: 'gray.400',
      }}
    >
      {children}
    </ChakraV3.chakra.li>
  );
});

export const SelectButton = React.forwardRef<
  HTMLButtonElement,
  ChakraV3.ButtonProps & {
    showArrow?: boolean;
    variant?: SelectButtonVariant;
    size?: ChakraV3.ButtonProps['size'];
    isDisabled?: boolean;
    isLoading?: boolean;
  }
>((props, ref) => {
  const { showArrow, isLoading, children, ...rest } = props;

  const height = P.run(() => {
    switch (props.size) {
      case 'xs':
        return 6;
      case 'sm':
        return 8;
      case 'md':
        return 9;
      case 'lg':
        return 12;
      default:
        return 6;
    }
  });

  return (
    <ChakraV3.Button
      ref={ref}
      as='div'
      disabled={props.isDisabled}
      size={props.size}
      variant={props.variant}
      w='full'
      role='group'
      data-pw='button'
      lineHeight='inherit'
      height='auto'
      minH={height}
      {...rest}
    >
      {children}
      {showArrow && (
        <>
          <ChakraV3.Spacer />
          <SelectStatus isLoading={isLoading} />
        </>
      )}
    </ChakraV3.Button>
  );
});

export const SelectInput = React.forwardRef<HTMLInputElement, ChakraV3.InputProps>((props, ref) => {
  return (
    <ChakraV3.Input
      ref={ref}
      size='sm'
      variant={'selectGhost' as ChakraV3.InputProps['variant']}
      bg='white'
      placeholder='Search...'
      px={3}
      {...props}
    />
  );
});

export const SelectStatus = (props: { isLoading?: boolean } & ChakraV3.BoxProps) => {
  const { isLoading, ...rest } = props;

  return (
    <ChakraV3.Box
      {...rest}
      visibility='hidden'
      _groupHover={{ visibility: 'visible' }}
      _groupFocusWithin={{ visibility: 'visible' }}
      display='flex'
      pointerEvents='none'
      alignItems='center'
      ml={1}
    >
      {isLoading ? (
        <Spin size='xs' />
      ) : (
        <ChakraV3.Icon fontSize='xs'>
          <FaCaretDown />
        </ChakraV3.Icon>
      )}
    </ChakraV3.Box>
  );
};

export const SelectCheck = () => {
  return (
    <ChakraV3.Icon
      visibility='hidden'
      fontSize='xs'
      _groupChecked={{
        visibility: 'visible',
      }}
    >
      <FaCheck />
    </ChakraV3.Icon>
  );
};

export const NoOptions = () => (
  <ChakraV3.Text
    px={2}
    py={1}
    fontSize='sm'
    fontWeight='semibold'
    color='gray.500'
    userSelect='none'
  >
    No results found
  </ChakraV3.Text>
);

export const OptionGroup = <A, B>(props: { group: SelectGroup<A, B> }) => {
  return (
    <ChakraV3.Text fontWeight='semibold' color='gray.500' py={2} px={1} fontSize='xs'>
      {props.group.label}
    </ChakraV3.Text>
  );
};

export const TagOption = <A,>(props: { option: SelectOption<A> }) => {
  const text =
    props.option.raw === 'CREATABLE' ? (
      <ChakraV3.Box display='inline-flex'>
        <ChakraV3.Text>Create</ChakraV3.Text>
        <ChakraV3.Text fontWeight='bold'>&nbsp;{`"${props.option.label}"`}</ChakraV3.Text>
      </ChakraV3.Box>
    ) : (
      <ChakraV3.Text>{props.option.label}</ChakraV3.Text>
    );

  return (
    <ChakraV3.HStack>
      <ChakraV3.Tag size='sm' variant='subtle' colorPalette={props.option.color}>
        <ChakraV3.TagLabel whiteSpace='normal'>{text}</ChakraV3.TagLabel>
      </ChakraV3.Tag>
      <ChakraV3.Spacer />
      <SelectCheck />
    </ChakraV3.HStack>
  );
};

export const TextOption = <A,>(props: { option: SelectOption<A> }) => {
  const text =
    props.option.raw === 'CREATABLE' ? (
      <ChakraV3.Box display='inline-flex'>
        <ChakraV3.Text>Create</ChakraV3.Text>
        <ChakraV3.Text fontWeight='bold'>&nbsp;{`"${props.option.label}"`}</ChakraV3.Text>
      </ChakraV3.Box>
    ) : (
      <ChakraV3.Text>{props.option.label}</ChakraV3.Text>
    );

  return (
    <ChakraV3.HStack>
      {text}
      <ChakraV3.Spacer />
      <SelectCheck />
    </ChakraV3.HStack>
  );
};

export const TagValue = <A,>(props: { option: SelectOption<A>; onRemove?: () => void }) => {
  return (
    <ChakraV3.Tag
      size='sm'
      variant='subtle'
      colorPalette={props.option?.color}
      closable={!!props.onRemove}
      onClose={props.onRemove}
    >
      {props.option.label}
    </ChakraV3.Tag>
  );
};

export const TextValue = <A,>(props: { option: SelectOption<A> }) => {
  return <ChakraV3.Text>{props.option.label}</ChakraV3.Text>;
};

export const Placeholder = (props: { placeholder?: string }) => {
  return (
    <ChakraV3.Text fontSize='sm' color='gray.500'>
      {props.placeholder ?? 'No value selected'}
    </ChakraV3.Text>
  );
};

export const Header = (props: ChakraV3.StackProps) => {
  return (
    <ChakraV3.HStack
      py={1.5}
      px={3}
      color='gray.500'
      borderBottomWidth='1px'
      borderBottomColor='gray.200'
      bg='white'
      w='full'
      justify='start'
      roundedTop='md'
      userSelect='none'
      tabIndex={-1}
      {...props}
    >
      {props.children}
    </ChakraV3.HStack>
  );
};

export const Footer = (props: ChakraV3.StackProps) => {
  return (
    <ChakraV3.HStack
      py='5px'
      px={3}
      bg='gray.50'
      fontSize='xs'
      borderTopWidth='1px'
      borderTopColor='gray.200'
      justify='start'
      w='full'
      roundedBottom='md'
      userSelect='none'
      tabIndex={-1}
    >
      {props.children}
    </ChakraV3.HStack>
  );
};

export const SelectComponents = {
  Loading,
  Error: SelectError,
  NoOptions,
  OptionGroup,
  Placeholder,
  Option: TagOption,
  Value: TagValue,
  Footer,
  Header,
};

const getComponents = <A, B, C extends Error>(
  components: Partial<SelectComponents<A, B, C>>,
  optionVariant: OptionVariant,
) => {
  return {
    ...SelectComponents,
    Option: optionVariant === 'tag' ? TagOption : TextOption,
    Value: optionVariant === 'tag' ? TagValue : TextValue,
    Footer: () => <></>,
    Header: undefined,
    ...components,
  };
};

export interface SelectProps<A, B, C extends Error = Error>
  extends Omit<ChakraV3.BoxProps, 'onChange'> {
  options: SelectOption<A>[] | SelectGroup<A, B>[];
  value: SelectOption<A> | null;
  inputValue?: string;
  variant?: ChakraV3.ButtonProps['variant'];
  size?: ChakraV3.ButtonProps['size'];
  optionVariant?: OptionVariant;
  placeholder?: string;
  placement?: Placement;
  isLoading?: boolean;
  error?: C | null;
  isDisabled?: boolean;
  showArrow?: boolean;
  components?: SelectComponents<A, B, C>;
  onChange: (value: SelectOption<A>) => void;
  onInputChange?: (value: string) => void;
  onOpen?: () => void;
}

export const Select = <A, B, C extends Error>(props: SelectProps<A, B, C>) => {
  const {
    options,
    value,
    inputValue,
    placeholder,
    isLoading = false,
    error,
    isDisabled,
    components,
    showArrow = true,
    size = 'sm',
    variant = 'selectOutline',
    optionVariant = 'text',
    placement = 'bottom-start',
    onChange,
    onInputChange,
    onOpen,
    ...rest
  } = props;

  const select = useSelect<A, B>({
    options,
    value,
    inputValue,
    isDisabled,
    onChange,
    onInputChange,
    onOpen,
  });

  const Components = getComponents(components ?? {}, optionVariant);

  const renderOption = (option: SelectOption<A>) => {
    return (
      <SelectItem key={option.value} {...select.optionProps(option)}>
        {Components.Option({ option })}
      </SelectItem>
    );
  };

  const renderGroup = (group: SelectGroup<A, B>) => {
    // don't render empty groups
    if (group.options.length === 0) {
      return null;
    }

    return (
      <ChakraV3.Box key={group.id}>
        {Components.OptionGroup({ group })}
        <ChakraV3.Box pl={1}>{group.options.map(renderOption)}</ChakraV3.Box>
      </ChakraV3.Box>
    );
  };

  return (
    <ChakraV3.Box className='group' {...select.menuProps()} w='full' {...rest}>
      <FloatingPopover
        open={select.isOpen}
        enabled={!isDisabled}
        placement={placement}
        isRoot={false}
        trigger='focus'
        render={() => (
          <SelectContainer>
            {Components.Header ? (
              Components.Header({})
            ) : (
              <ChakraV3.Box borderBottomWidth='1px'>
                <SelectInput {...select.inputProps()} />
              </ChakraV3.Box>
            )}
            <ScrollArea maxH='xs' overflow='auto'>
              <ChakraV3.chakra.ul p={1.5} data-pw='menu'>
                {error && Components.Error({ error })}
                {isLoading && !error && select.options.length === 0 && Components.Loading()}
                {!isLoading && !error && select.options.length === 0 && Components.NoOptions()}
                {!error &&
                  select.options.map((option) =>
                    'options' in option ? renderGroup(option) : renderOption(option),
                  )}
              </ChakraV3.chakra.ul>
            </ScrollArea>
            {Components.Footer({})}
          </SelectContainer>
        )}
      >
        <ChakraV3.Box tabIndex={-1}>
          <SelectButton
            {...select.buttonProps()}
            variant={variant}
            size={size}
            showArrow={showArrow}
            isDisabled={isDisabled}
            isLoading={isLoading}
          >
            {P.isNil(select.value)
              ? Components.Placeholder({ placeholder })
              : Components.Value({ option: select.value })}
          </SelectButton>
        </ChakraV3.Box>
      </FloatingPopover>
    </ChakraV3.Box>
  );
};
